// Single source of truth for the funnel survey.
// Imported by both the live Survey component (`src/components/funnel/Survey.tsx`)
// and the admin visualizer (`src/routes/admin.survey.tsx`).
//
// To keep the admin view in sync with the actual survey:
//   - Add / edit / reorder questions HERE.
//   - Express conditional logic via the `conditions` field (data, not code),
//     so the admin tab can render it visually.
//   - Answer scores live on each option as `score` (hidden from the user).
//     Routing/tier logic lives in `src/lib/survey-routing.ts`.

export type SurveyAnswerValue = string | string[];
export type SurveyAnswers = Record<string, SurveyAnswerValue>;

export type SurveyChoice = {
  value: string;
  label: string;
  hint?: string;
  /** Hidden scoring weight. Never rendered to the respondent. */
  score?: number;
};

/**
 * A single "show this step when ..." rule. All rules in `conditions` must
 * match (AND).
 *   - `equals`: single-value question's answer must be one of these values.
 *   - `includesAny`: multi-select question's answer array must contain at
 *     least one of these values.
 */
export type SurveyCondition = {
  /** id of a previous question */
  questionId: string;
  equals?: string[];
  includesAny?: string[];
  /** multi-select: passes when answer array contains NONE of these values. */
  excludesAll?: string[];
};

type SurveyStepBase = {
  id: string;
  question: string;
  sub?: string;
  /** Optional bullet list rendered under the question text. */
  bullets?: string[];
  /** Optional conditional visibility. If omitted, the step is always shown. */
  conditions?: SurveyCondition[];
  /** Optional terminal action after this step (overrides the default outcome). */
  terminal?: { outcomeId: string };
  /** If set, the terminal only applies when ALL of these conditions match (AND). */
  terminalWhen?: SurveyCondition[];
  /** If set, ALSO requires at least one of these conditions to match (OR). */
  terminalWhenAny?: SurveyCondition[];
};

export type SurveyStep =
  | (SurveyStepBase & { kind: "choice"; options: SurveyChoice[] })
  | (SurveyStepBase & {
      kind: "multi_choice";
      options: SurveyChoice[];
      required?: boolean;
      minSelected?: number;
    })
  | (SurveyStepBase & {
      kind: "input";
      type?: "text" | "tel" | "email";
      placeholder?: string;
      required?: boolean;
      /** Small note rendered directly BELOW the input field. */
      note?: string;
    })
  | (SurveyStepBase & { kind: "textarea"; placeholder?: string; required?: boolean });

/** Outcome = where a respondent lands after the final visible step. */
export type SurveyOutcome = {
  id: string;
  label: string;
  description: string;
  /** "calendar" embeds a GHL calendar iframe; "redirect" sends to a URL/route. */
  kind: "calendar" | "redirect" | "page";
  /** For calendar: GHL widget src (without query params). For redirect/page: the URL or route. */
  target: string;
};

export const OUTCOMES: Record<string, SurveyOutcome> = {
  discovery_call: {
    id: "discovery_call",
    label: "Intro / Discovery Call Calendar (Tier 2 — Maybe)",
    description:
      "Embedded GHL booking widget for the Intro (Discovery) Call. Shown to Tier 2 leads: clinic owner, valid modality, care plan $2,000+, but not a clean 3 across Q3/Q4.",
    kind: "calendar",
    target: "https://api.leadconnectorhq.com/widget/booking/t88z0rTF8qANHWLW2M0k",
  },
  demo_call: {
    id: "demo_call",
    label: "Demo Call Calendar (Tier 3 — Qualified)",
    description:
      "Embedded GHL booking widget for the Demo Call. Shown only when Q3 = A and Q4 = A (score 3 across the board).",
    kind: "calendar",
    target: "https://api.leadconnectorhq.com/widget/booking/8ybq23aBVgZCb0VWlmtG",
  },
  dq_modal: {
    id: "dq_modal",
    label: "Disqualification Page",
    description:
      "Polite 'not a fit right now' screen with no booking. Triggered by: not a clinic owner (Q1 B/C) or a highest care plan under $1,500 (Q3 E).",
    kind: "page",
    target: "modal",
  },
};

/** Copy for the in-survey disqualification screen (outcome `dq_modal`). */
export const DQ_MODAL_CONTENT = {
  title: "You're not a fit right now",
  body:
    "Based on your answers, we're not the right fit right now. We work with cash-pay clinics running decompression, regen, knee, or neuropathy with care plans of $3,000 or more. If that changes, come back and apply again.",
  ctaLabel: "Back to the site",
  ctaHref: "/vsl-v3",
};

export const SURVEY_DEFAULT_OUTCOME = "discovery_call";

export const SURVEY_STEPS: SurveyStep[] = [
  // Q1 — ownership gate. B/C disqualify immediately.
  {
    id: "clinic_owner",
    kind: "choice",
    question: "Do you own a Chiropractic or Medical Clinic?",
    sub: "If Not - STOP AND LEAVE THIS PAGE",
    options: [
      { value: "Yes - I'm a Clinic Owner", label: "Yes - I'm a Clinic Owner", score: 3 },
      { value: "No - I'm going to leave this page", label: "No - I'm going to leave this page", score: 0 },
      { value: "I'm a patient looking for a doctor", label: "I'm a patient looking for a doctor", score: 0 },
    ],
  },
  // Q2 — services offered. Always shown.
  {
    id: "offers_treatment",
    kind: "choice",
    question: "Do you offer any of these services in your clinic:",
    bullets: [
      "Spinal Decompression",
      "Regenerative Medicine",
      "Knee Pain treatment",
      "Neuropathy treatment",
    ],
    options: [
      {
        value: "Yes, I offer it now and have the equipment for it",
        label: "Yes, I offer it now and have the equipment for it",
        score: 3,
      },
      {
        value: "I have the equipment but I'm not actively running it now",
        label: "I have the equipment but I'm not actively running it now",
        score: 3,
      },
      {
        value: "I don't offer it yet, but I'm looking to add it",
        label: "I don't offer it yet, but I'm looking to add it",
        score: 2,
      },
      {
        value: "No, I mainly do general chiropractic, adjustments, or PI",
        label: "No, I mainly do general chiropractic, adjustments, or PI",
        score: 1,
      },
    ],
  },
  // Q3 — care plan price. Always shown.
  {
    id: "care_plan_price",
    kind: "choice",
    question: "What's the price of your highest care plan?",
    options: [
      { value: "$10,000+", label: "$10,000+", score: 3 },
      { value: "$5,000 to $10,000", label: "$5,000 to $10,000", score: 3 },
      { value: "$3,000 to $5,000", label: "$3,000 to $5,000", score: 3 },
      { value: "$1,500 to $2,999", label: "$1,500 to $2,999", score: 2 },
      { value: "Less than $1,500", label: "Less than $1,500", score: 1 },
    ],
  },
  // Contact capture — only reached on non-disqualified paths.
  {
    id: "name",
    kind: "input",
    type: "text",
    question: "What's your full name?",
    placeholder: "Enter your full name...",
    required: true,
  },
  {
    id: "phone",
    kind: "input",
    type: "tel",
    question: "What's your best mobile number?",
    placeholder: "Enter your mobile number...",
    required: true,
  },
  {
    id: "email",
    kind: "input",
    type: "email",
    question: "What's your email?",
    placeholder: "Enter your email here...",
    required: true,
    note: "Don't worry, we don't spam :)",
  },
];

/** Question text as rendered (no interpolation tokens remain in the survey). */
export function renderQuestionText(question: string, _answers: SurveyAnswers): string {
  return question;
}


/** Build a runtime `when(answers)` predicate from declarative conditions. */
export function buildWhen(
  conditions?: SurveyCondition[],
): ((a: SurveyAnswers) => boolean) | undefined {
  if (!conditions || conditions.length === 0) return undefined;
  return (a) =>
    conditions.every((c) => {
      const v = a[c.questionId];
      if (c.includesAny && c.includesAny.length > 0) {
        if (!Array.isArray(v)) return false;
        return c.includesAny.some((x) => v.includes(x));
      }
      if (c.excludesAll && c.excludesAll.length > 0) {
        const arr = Array.isArray(v) ? v : [];
        return c.excludesAll.every((x) => !arr.includes(x));
      }
      if (c.equals && c.equals.length > 0) {
        if (v == null) return false;
        if (Array.isArray(v)) return v.some((x) => c.equals!.includes(x));
        return c.equals.includes(v);
      }
      return true;
    });
}

/** Build an OR predicate: any one of the given conditions must match. */
function buildWhenAny(
  conditions?: SurveyCondition[],
): ((a: SurveyAnswers) => boolean) | undefined {
  if (!conditions || conditions.length === 0) return undefined;
  return (a) => conditions.some((c) => buildWhen([c])!(a));
}

/** Resolve which outcome a given answer-path lands on. */
export function resolveOutcome(answers: SurveyAnswers): SurveyOutcome {
  // Walk visible steps in order and use the last terminal override if present.
  let outcomeId = SURVEY_DEFAULT_OUTCOME;
  for (const step of SURVEY_STEPS) {
    const when = buildWhen(step.conditions);
    if (when && !when(answers)) continue;
    if (!step.terminal?.outcomeId) continue;
    const terminalWhen = buildWhen(step.terminalWhen);
    if (terminalWhen && !terminalWhen(answers)) continue;
    const terminalWhenAny = buildWhenAny(step.terminalWhenAny);
    if (terminalWhenAny && !terminalWhenAny(answers)) continue;
    outcomeId = step.terminal.outcomeId;
  }
  return OUTCOMES[outcomeId] ?? OUTCOMES[SURVEY_DEFAULT_OUTCOME];
}
